import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Copy, Download, Eraser, FileText, AlertTriangle, Save, ShieldQuestion, Gauge, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { SegmentCard } from "./SegmentCard";
import { LayoverDivider } from "./LayoverDivider";
import ModelSummary from "./ModelSummary";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { SABRE_SAMPLE, AMADEUS_SAMPLE, MULTI_SERVICE_SAMPLE, formatItineraryText } from "../lib/pnrUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gdsBadgeClass = {
  Sabre: "bg-indigo-100 text-indigo-800 border-indigo-200",
  Amadeus: "bg-teal-100 text-teal-800 border-teal-200",
};

const confidenceBadgeClass = (level) => {
  switch (level) {
    case "HIGH":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "MEDIUM":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "LOW":
      return "bg-orange-100 text-orange-800 border-orange-200";
    default:
      return "bg-red-100 text-red-800 border-red-200";
  }
};

export default function PnrConverter({ layout, dark = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [savingItin, setSavingItin] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleConvert = async (override) => {
    const source = typeof override === "string" ? override : text;
    if (!source.trim()) {
      toast.error("Please paste a PNR / itinerary first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/parse`, { text: source });
      setResult(data);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Parsed ${data.segments.length} segment(s) — ${data.gds} format detected.`);
      }
    } catch (e) {
      toast.error("Failed to convert. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadDemoTrip = () => {
    setText(MULTI_SERVICE_SAMPLE);
    handleConvert(MULTI_SERVICE_SAMPLE);
  };

  const handleCopy = async () => {
    if (!result?.segments?.length) return;
    await navigator.clipboard.writeText(formatItineraryText(result.gds, result.segments));
    toast.success("Copied to clipboard!");
  };

  const handleExport = () => {
    if (!result?.segments?.length) return;
    const blob = new Blob([formatItineraryText(result.gds, result.segments)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "itinerary.txt";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Itinerary exported.");
  };

  const clearAll = () => {
    setText("");
    setResult(null);
  };

  const saveAsItinerary = async () => {
    if (!result?.segments?.length) return;
    if (!user) {
      toast.error("Please log in to save itineraries.");
      navigate("/login");
      return;
    }
    setSavingItin(true);
    try {
      const dest = result.segments[result.segments.length - 1]?.destination_city || "";
      const { data } = await api.post("/itineraries", {
        title: dest ? `Trip to ${dest}` : "New itinerary",
        gds: result.gds,
        segments: result.segments,
        destination_city: dest,
        extras: result.extras || {},
        model: result.model || null,
        layout,
      });
      toast.success("Saved! Opening the builder…");
      navigate(`/itineraries/${data.id}`);
    } catch (e) {
      toast.error("Could not save itinerary.");
    } finally {
      setSavingItin(false);
    }
  };

  const hasSegments = result?.segments?.length > 0;

  const buildManually = async () => {
    if (!user) {
      toast.error("Please log in to build an itinerary.");
      navigate("/login");
      return;
    }
    setSavingItin(true);
    try {
      const { data } = await api.post("/itineraries", {
        title: "New itinerary",
        gds: "",
        segments: [],
        destination_city: "",
        extras: {},
        model: null,
        layout,
      });
      toast.success("Blank itinerary created — start building!");
      navigate(`/itineraries/${data.id}`);
    } catch (e) {
      toast.error("Could not create itinerary.");
    } finally {
      setSavingItin(false);
    }
  };

  return (
    <div className={`rounded-2xl border p-4 sm:p-6 lg:p-8 ${dark ? "bg-slate-900 border-slate-700" : "bg-[#F8FAFC] border-slate-200/60"}`} data-testid="converter-tool-surface">
      <div className="grid grid-cols-1 gap-6">
          {/* Input */}
          <section className="flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className={`font-heading font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>Raw itinerary</h2>
              <div className="flex items-center gap-1.5">
                <button
                  data-testid="sample-multiservice-btn"
                  onClick={loadDemoTrip}
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Try a sample trip
                </button>
                <button
                  data-testid="sample-sabre-btn"
                  onClick={() => setText(SABRE_SAMPLE)}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${dark ? "border-slate-600 text-slate-300 hover:bg-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}
                >
                  Sabre sample
                </button>
                <button
                  data-testid="sample-amadeus-btn"
                  onClick={() => setText(AMADEUS_SAMPLE)}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${dark ? "border-slate-600 text-slate-300 hover:bg-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}
                >
                  Amadeus sample
                </button>
              </div>
            </div>
            <Textarea
              data-testid="pnr-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={"Paste your raw GDS itinerary here...\n\n 1 UA 456K 12DEC J EWRSFO HK1  600P  920P"}
              className={`font-pnr text-sm h-[196px] min-h-[196px] resize-none overflow-y-auto focus-visible:ring-2 focus-visible:ring-blue-500 ${dark ? "bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500" : "bg-white border-slate-200 text-slate-900"}`}
            />
            <div className="flex items-center gap-2 mt-4">
              <Button
                data-testid="convert-btn"
                onClick={handleConvert}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium active:scale-[0.98] transition-transform"
              >
                <Zap className="h-4 w-4 mr-1.5" />
                {loading ? "Converting..." : "Convert"}
              </Button>
              <Button
                data-testid="clear-btn"
                onClick={clearAll}
                variant="outline"
                className="border-slate-200 text-slate-600"
              >
                <Eraser className="h-4 w-4 mr-1.5" />
                Clear
              </Button>
              {layout && user && (
                <Button
                  data-testid="build-manually-btn"
                  onClick={buildManually}
                  disabled={savingItin}
                  variant="outline"
                  className="ml-auto border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <FileText className="h-4 w-4 mr-1.5" />
                  {savingItin ? "Creating…" : "Build manually / Skip"}
                </Button>
              )}
            </div>
          </section>

          {/* Output */}
          {result && (
          <section className="flex flex-col">
            <div className="flex items-center justify-between mb-3 min-h-[30px]">
              <div className="flex items-center gap-2">
                <h2 className={`font-heading font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>Itinerary</h2>
                {result?.gds && (
                  <Badge
                    data-testid="gds-badge"
                    variant="outline"
                    className={`rounded-full font-medium ${gdsBadgeClass[result.gds] || ""}`}
                  >
                    {result.gds}
                  </Badge>
                )}
                {result?.model?.confidence && (
                  <Badge
                    data-testid="model-confidence"
                    variant="outline"
                    className={`rounded-full font-medium inline-flex items-center gap-1 ${confidenceBadgeClass(result.model.confidence.level)}`}
                    title={`Overall parse confidence ${Math.round((result.model.confidence.overall || 0) * 100)}%`}
                  >
                    <Gauge className="h-3 w-3" />
                    {result.model.confidence.level} · {Math.round((result.model.confidence.overall || 0) * 100)}%
                  </Badge>
                )}
              </div>
              {hasSegments && (
                <div className="flex items-center gap-1.5">
                  <Button data-testid="save-itinerary-btn" onClick={saveAsItinerary} disabled={savingItin || !hasSegments} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Save className="h-4 w-4 mr-1.5" /> {savingItin ? "Saving…" : "Save as itinerary"}
                  </Button>
                  <Button data-testid="copy-btn" onClick={handleCopy} variant="ghost" size="sm" className="text-slate-600 hover:bg-slate-100">
                    <Copy className="h-4 w-4 mr-1.5" /> Copy
                  </Button>
                  <Button data-testid="export-btn" onClick={handleExport} variant="ghost" size="sm" className="text-slate-600 hover:bg-slate-100">
                    <Download className="h-4 w-4 mr-1.5" /> Export
                  </Button>
                </div>
              )}
            </div>

            {result?.warnings?.length > 0 && (
              <div data-testid="parse-warnings" className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Some codes were not in the database (shown as raw codes):</div>
                  <ul className="list-disc list-inside mt-1 text-amber-700">
                    {result.warnings.map((w, i) => <li key={`${i}:${w}`}>{w}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {result?.error && (
              <div data-testid="parse-error" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                {result.error}
              </div>
            )}

            {result?.model?.unknownSegments?.length > 0 && !result?.model?.errors && (
              <div data-testid="unrecognized-gds" className={`mb-4 rounded-lg border p-3 text-sm ${dark ? "border-slate-700 bg-slate-800/60 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                <div className="flex items-center gap-2 font-medium mb-1">
                  <ShieldQuestion className="h-4 w-4 shrink-0 text-slate-400" />
                  Unrecognized GDS Information ({result.model.unknownSegments.length})
                </div>
                <ul className="space-y-1">
                  {result.model.unknownSegments.map((u, i) => (
                    <li key={`unk-${i}`} data-testid="unrecognized-item" className={`font-pnr text-xs rounded px-2 py-1 break-words ${dark ? "bg-slate-900 text-slate-300" : "bg-white text-slate-700 border border-slate-100"}`}>
                      {u.raw}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <AnimatePresence>
                {hasSegments &&
                  result.segments.map((seg, i) => (
                    <div key={`${seg.airline_code || ""}${seg.flight_number || ""}${seg.date_raw || ""}-${i}`} className="space-y-3">
                      <SegmentCard segment={seg} index={i} />
                      <LayoverDivider segment={seg} next={result.segments[i + 1]} index={i} />
                    </div>
                  ))}
              </AnimatePresence>
              {result?.model && <ModelSummary model={result.model} dark={dark} />}
            </div>
          </section>
          )}
        </div>
    </div>
  );
}
